import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, ShoppingOutlined } from '@ant-design/icons';
import { Button, Typography, Spin, Empty } from 'antd';
import dayjs from 'dayjs';
import { useOrders } from '@hooks';

const { Title, Paragraph } = Typography;

export const OrderPage = () => {
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('accessToken');
  
  const { getOrder } = useOrders();
  const { data: order, isLoading, isError } = getOrder(orderId || '', accessToken || undefined);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const getStatusColor = (status: string) => {
    const statusColors: Record<string, string> = {
      pending: 'bg-[#00ade6]/20 text-[#00ade6]',
      paid: 'bg-green-500/20 text-green-500',
      processing: 'bg-blue-500/20 text-blue-500',
      shipped: 'bg-purple-500/20 text-purple-500',
      delivered: 'bg-green-600/20 text-green-600',
      cancelled: 'bg-red-500/20 text-red-500',
    };
    return statusColors[status.toLowerCase()] || 'bg-gray-500/20 text-gray-500';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" />
          <Paragraph className="font-android text-white text-xl mt-4">
            Loading order details...
          </Paragraph>
        </div>
      </div>
    );
  }

  if (isError || !order) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <Empty
            description={
              <span className="font-android text-white text-xl">
                Order not found
              </span>
            }
          />
          <Paragraph className="font-android text-zinc-400 mt-4 mb-6">
            The order you're looking for doesn't exist or you don't have permission to view it.
          </Paragraph>
          <Button
            type="primary"
            size="large"
            onClick={() => navigate('/merch')}
            className="btn-single-cut bg-[#00ade6] border-none font-azonix font-normal text-xl"
          >
            Return to Store
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <section className="bg-zinc-900 py-8">
        <div className="container mx-auto px-4">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/merch')}
            className="text-[#00ade6] hover:text-[#0095c7] mb-4 font-android"
          >
            Back to Store
          </Button>
        </div>
      </section>

      {/* Order Details */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="bg-zinc-900 border-2 border-zinc-700 rounded-xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#00ade6] to-[#0095c7] px-8 py-6">
              <Title level={2} className="font-android !text-black uppercase mb-0 !text-3xl">
                ORDER #{order.orderNumber}
              </Title>
            </div>

            {/* Order Information Grid */}
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-5">
                  <Paragraph className="font-android text-[#00ade6] text-sm uppercase mb-2 font-bold">
                    Order Number
                  </Paragraph>
                  <Paragraph className="font-android text-white text-xl mb-0 font-bold">
                    {order.orderNumber}
                  </Paragraph>
                </div>

                <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-5">
                  <Paragraph className="font-android text-[#00ade6] text-sm uppercase mb-2 font-bold">
                    Date
                  </Paragraph>
                  <Paragraph className="font-android text-white text-xl mb-0 font-bold">
                    {dayjs(order.createdAt).format('MMM DD, YYYY')}
                  </Paragraph>
                  <Paragraph className="font-android text-zinc-400 text-sm mb-0">
                    {dayjs(order.createdAt).format('HH:mm')}
                  </Paragraph>
                </div>

                <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-5">
                  <Paragraph className="font-android text-[#00ade6] text-sm uppercase mb-2 font-bold">
                    Email
                  </Paragraph>
                  <Paragraph className="font-android text-white text-lg mb-0 break-all">
                    {order.customerEmail}
                  </Paragraph>
                </div>
              </div>

              {/* Status Badge */}
              <div className="mb-8">
                <Paragraph className="font-android text-[#00ade6] text-sm uppercase mb-3 font-bold">
                  Status
                </Paragraph>
                <span 
                  className={`inline-block px-6 py-3 ${getStatusColor(order.status)} rounded-full font-android font-bold uppercase text-lg border-2 border-current`}
                >
                  {order.status}
                </span>
              </div>

              {/* Items Table */}
              <div className="mb-8">
                <Title level={3} className="font-android text-white uppercase mb-4 !text-2xl">
                  Items Ordered
                </Title>
                <div className="bg-zinc-800 border-2 border-zinc-700 rounded-lg overflow-hidden">
                  {/* Table Header */}
                  <div className="grid grid-cols-12 gap-4 bg-zinc-900 px-6 py-4 border-b-2 border-[#00ade6]">
                    <div className="col-span-5 font-android text-[#00ade6] uppercase font-bold text-sm">
                      Product
                    </div>
                    <div className="col-span-2 font-android text-[#00ade6] uppercase font-bold text-sm text-center">
                      Quantity
                    </div>
                    <div className="col-span-2 font-android text-[#00ade6] uppercase font-bold text-sm text-right">
                      Price
                    </div>
                    <div className="col-span-3 font-android text-[#00ade6] uppercase font-bold text-sm text-right">
                      Subtotal
                    </div>
                  </div>

                  {/* Table Body */}
                  {order.items.map((item, index) => (
                    <div 
                      key={item.id}
                      className={`grid grid-cols-12 gap-4 px-6 py-5 ${
                        index < order.items.length - 1 ? 'border-b border-zinc-700' : ''
                      } hover:bg-zinc-750 transition-colors`}
                    >
                      <div className="col-span-5 font-android text-white text-lg font-bold">
                        {item.productName}
                      </div>
                      <div className="col-span-2 font-android text-white text-lg text-center">
                        {item.quantity}
                      </div>
                      <div className="col-span-2 font-android text-[#00ade6] font-bold text-lg text-right">
                        {formatPrice(item.price)}
                      </div>
                      <div className="col-span-3 font-android text-[#00ade6] font-bold text-lg text-right">
                        {formatPrice(item.subtotal)}
                      </div>
                    </div>
                  ))}

                  {/* Subtotal */}
                  <div className="border-t border-zinc-700 px-6 py-4">
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-9 font-android text-zinc-400 uppercase font-bold text-lg text-right">
                        Subtotal:
                      </div>
                      <div className="col-span-3 font-android text-zinc-400 font-bold text-lg text-right">
                        {formatPrice(order.subtotalBeforeDiscount)}
                      </div>
                    </div>
                  </div>

                  {/* Discount (only show if there's a discount) */}
                  {order.discountAmount > 0 && (
                    <div className="px-6 py-4 bg-green-500/10">
                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-9 font-android text-green-500 uppercase font-bold text-lg text-right">
                          Tier Discount:
                        </div>
                        <div className="col-span-3 font-android text-green-500 font-bold text-lg text-right">
                          -{formatPrice(order.discountAmount)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Total */}
                  <div className="bg-[#00ade6] px-6 py-5">
                    <div className="grid grid-cols-12 gap-4">
                      <div className="col-span-9 font-android text-black uppercase font-bold text-xl text-right">
                        TOTAL:
                      </div>
                      <div className="col-span-3 font-android text-black font-bold text-2xl text-right">
                        {formatPrice(order.totalAmount)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  type="primary"
                  size="large"
                  icon={<ShoppingOutlined />}
                  onClick={() => navigate('/merch')}
                  className="bg-[#00ade6] hover:bg-[#0095c7] border-none font-android text-xl h-16 px-8 shadow-lg shadow-[#00ade6]/30 hover:shadow-[#00ade6]/50 transition-all"
                >
                  Continue Shopping
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

